import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import React, { useEffect, useMemo, useState } from 'react';

import { calculatePercentage, getOccupancyBandColor } from '@app/containers/components/OccupancyRate';
import { Callout, Card, Classes, HTMLSelect, HTMLTable, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import DevText from '@components/Text';

import { TeamPeriodOccupancyResource, fetchTeamPeriodOccupancy, fetchTeamPeriodOccupancyList } from '../../data/api';
import { Controls, TableScroll, Toolbar } from '../../styled';
import {
    buildParticipantScoreMap,
    calculateOverallScore,
    collectMembers,
    filterMembersByRoles,
    filterMembersByTeam,
    getDefaultWeeklyPeriod,
    listMemberRoles,
    listWeeks,
    snapToWholeWeeks,
    validatePeriod,
} from '../../utils/teamHealth';
import MemberIdentity, { getMemberRole } from '../MemberIdentity';
import OccupancyBars from '../OccupancyBars';
import RoleFilter, { RoleSelectionProps } from '../RoleFilter';
import WeeklyChart from '../WeeklyChart';

export interface WeeklyOccupancyProps extends RoleSelectionProps {
    teams: TeamResource[];
}

type WeeklyResult =
    | { key: string; range: TeamPeriodOccupancyResource[]; weeks: TeamPeriodOccupancyResource[][] }
    | { key: string; error: string };

const ALL_TEAMS = '';

const WeeklyOccupancy: React.FC<WeeklyOccupancyProps> = ({ teams, selectedRoles, onSelectedRolesChange }) => {
    const [defaultPeriod] = useState(() => getDefaultWeeklyPeriod(new Date()));
    const [teamId, setTeamId] = useState(ALL_TEAMS);
    const [fromInput, setFromInput] = useState(defaultPeriod.fromDate);
    const [toInput, setToInput] = useState(defaultPeriod.toDate);
    const [result, setResult] = useState<WeeklyResult | null>(null);

    // Requested dates widen to whole Monday–Sunday weeks; an invalid range is not requested at all
    const period = snapToWholeWeeks({ fromDate: fromInput, toDate: toInput });
    const validationMessage = validatePeriod(period);
    const weeks = useMemo(
        () => (validationMessage === null ? listWeeks(period) : []),
        [validationMessage, period.fromDate, period.toDate]
    );

    const selectedTeams = useMemo(
        () => (teamId === ALL_TEAMS ? teams : teams.filter((team) => team.id === teamId)),
        [teams, teamId]
    );
    const requestKey = `${teamId}|${period.fromDate}|${period.toDate}`;

    useEffect(() => {
        if (validationMessage !== null) {
            return;
        }

        let isCancelled = false;
        const teamIds = selectedTeams.map((team) => team.id);
        setResult(null);
        Promise.all([fetchTeamPeriodOccupancy(teamIds, period), fetchTeamPeriodOccupancyList(teamIds, weeks)])
            .then(([range, weekItems]) => !isCancelled && setResult({ key: requestKey, range, weeks: weekItems }))
            .catch((e) => {
                console.error(e);
                if (!isCancelled) {
                    setResult({ key: requestKey, error: 'Occupancy for these weeks could not be loaded.' });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [requestKey, validationMessage, selectedTeams]);

    const members = useMemo(() => collectMembers(teams), [teams]);
    const roles = useMemo(() => listMemberRoles(members), [members]);

    // Only render data that answers the team and weeks currently selected
    const current = result && result.key === requestKey ? result : null;
    const data = current && 'range' in current ? current : null;

    const rangeScores = useMemo(() => {
        const scores: Record<string, number> = {};
        (data?.range ?? []).forEach((item) => (scores[item.team_id] = item.score));
        return scores;
    }, [data]);
    const weekTeamScores = useMemo(
        () =>
            (data?.weeks ?? []).map((items) => {
                const scores: Record<string, number> = {};
                items.forEach((item) => (scores[item.team_id] = item.score));
                return scores;
            }),
        [data]
    );
    const weekParticipantScores = useMemo(() => (data?.weeks ?? []).map(buildParticipantScoreMap), [data]);

    const inputClassName = `${Classes.INPUT} ${validationMessage ? Classes.INTENT_DANGER : ''}`;

    const renderControls = () => (
        <Controls>
            <div>
                <label htmlFor="team-health-team">Team</label>
                <HTMLSelect id="team-health-team" value={teamId} onChange={(e) => setTeamId(e.currentTarget.value)}>
                    <option value={ALL_TEAMS}>All Teams</option>
                    {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                            {team.title}
                        </option>
                    ))}
                </HTMLSelect>
            </div>
            <RoleFilter roles={roles} selectedRoles={selectedRoles} onSelectedRolesChange={onSelectedRolesChange} />
            <div>
                <label htmlFor="team-health-from">From date</label>
                <input
                    id="team-health-from"
                    type="date"
                    className={inputClassName}
                    value={fromInput}
                    onChange={(e) => setFromInput(e.target.value)}
                />
            </div>
            <div>
                <label htmlFor="team-health-to">To date</label>
                <input
                    id="team-health-to"
                    type="date"
                    className={inputClassName}
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                />
            </div>
        </Controls>
    );

    const renderChartPanel = () => {
        if (validationMessage !== null) {
            return <Callout intent={Intent.WARNING}>{validationMessage}</Callout>;
        }

        if (current && 'error' in current) {
            return <Callout intent={Intent.DANGER}>{current.error}</Callout>;
        }

        if (!data) {
            return (
                <Flex justify="center">
                    <Spinner size={30} />
                </Flex>
            );
        }

        const rangeTeamScores = selectedTeams
            .filter((team) => team.id in rangeScores)
            .map((team) => rangeScores[team.id]);

        return (
            <>
                <div className="mb-2">
                    <OccupancyBars overallScore={calculateOverallScore(rangeTeamScores)} items={[]} selectedId={null} />
                </div>
                <WeeklyChart
                    teams={selectedTeams.map((team) => ({ id: team.id, title: team.title, color: team.color }))}
                    weeks={weeks}
                    scores={weekTeamScores}
                />
            </>
        );
    };

    const renderWeekCell = (weekIndex: number, userId: string) => {
        const score = weekParticipantScores[weekIndex]?.[userId];
        if (score === undefined) {
            return (
                <DevText inline muted>
                    {data ? 'Not scored' : '…'}
                </DevText>
            );
        }

        return (
            <DevText inline color={getOccupancyBandColor(score)}>
                {calculatePercentage(score)}%
            </DevText>
        );
    };

    const visibleMembers = filterMembersByRoles(
        filterMembersByTeam(members, teamId === ALL_TEAMS ? null : teamId),
        selectedRoles
    );

    return (
        <>
            <Toolbar>
                <DevText muted>
                    {validationMessage === null ?
                        `${weeks.length} week${weeks.length === 1 ? '' : 's'}: ${period.fromDate} – ${period.toDate} (UTC, Monday–Sunday)`
                    :   'Choose a valid range of weeks'}
                </DevText>
                {renderControls()}
            </Toolbar>

            <Card className="mb-2">{renderChartPanel()}</Card>

            {validationMessage === null && (
                <Card>
                    <TableScroll>
                        <HTMLTable striped>
                            <thead>
                                <tr>
                                    <th>Member</th>
                                    <th>Role</th>
                                    <th>Team</th>
                                    {weeks.map((week) => (
                                        <th key={week.fromDate}>{week.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleMembers.map((member) => (
                                    <tr key={member.user.id}>
                                        <td>
                                            <MemberIdentity user={member.user} />
                                        </td>
                                        <td>{getMemberRole(member.user)}</td>
                                        <td>{member.teams.map((team) => team.title).join(', ')}</td>
                                        {weeks.map((week, weekIndex) => (
                                            <td key={week.fromDate}>{renderWeekCell(weekIndex, member.user.id)}</td>
                                        ))}
                                    </tr>
                                ))}
                                {visibleMembers.length === 0 && (
                                    <tr>
                                        <td colSpan={3 + weeks.length}>
                                            <DevText muted>
                                                {selectedRoles.length > 0 ?
                                                    'No members match the selected roles.'
                                                :   'No active members.'}
                                            </DevText>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </HTMLTable>
                    </TableScroll>
                </Card>
            )}
        </>
    );
};

export default WeeklyOccupancy;
